import { createSelector } from 'reselect';
import isNil from 'ramda/src/isNil';
import moment from 'moment';
import CampaignService from 'mangools-commons/dist/services/CampaignService';
import DateTimeService from 'mangools-commons/dist/services/DateTimeService';
import Defaults from 'mangools-commons/dist/constants/Defaults';

import UserPlanTypes from 'constants/UserPlanTypes';
import { trackedKeywordsCountSelector, trackingsCountSelector } from 'selectors/commonSelectors';

export const accessTokenSelector = state => state.user.accessToken;
export const onDemandRankScansRemainingSelector = state => state.user.limits.onDemandRankScansRemaining;
export const createdAtSelector = state => state.user.createdAt;
export const fetchedSelector = state => state.user.fetched;
export const idSelector = state => state.user.id;
export const emailSelector = state => state.user.email;
export const limitsFetchedSelector = state => state.user.limits.fetched;
export const loggedInSelector = state => state.user.loggedIn;
export const ssoTicketSelector = state => state.user.ssoTicket;
export const trackedKeywordLimitSelector = state => state.user.limits.trackedKeywordLimit;
export const trackingsLimitSelector = state => state.user.limits.trackings;
export const userPlanSelector = state => state.user.plan;
export const loginTokenSelector = state => state.user.loginToken;
export const unleashSessionSelector = state => state.user.unleashSession;
export const isSubUserSelector = state => state.user.isSubUser;
export const isStaffSelector = state => state.user.isStaff;
export const settingsSelector = state => state.user.settings;

export const remainingTrackedKeywordLimitSelector = createSelector(
    [trackedKeywordsCountSelector, trackedKeywordLimitSelector],
    (trackedCount, limit) => {
        const remaining = limit - trackedCount;
        return remaining < 0 ? 0 : remaining;
    },
);

export const remainingTrackingsLimitSelector = createSelector(
    [trackingsCountSelector, trackingsLimitSelector],
    (trackingsCount, limit) => {
        const remaining = limit - trackingsCount;
        return remaining < 0 ? 0 : remaining;
    },
);

export const userPlanTypeSelector = createSelector([loggedInSelector, userPlanSelector], (loggedIn, userPlan) => {
    if (loggedIn === false || isNil(userPlan)) {
        return UserPlanTypes.NO_PLAN;
    } else {
        switch (userPlan) {
            case 'free': {
                return UserPlanTypes.FREE;
            }
            case 'trial': {
                return UserPlanTypes.TRIAL;
            }
            case 'entry': {
                return UserPlanTypes.ENTRY;
            }
            case 'basic': {
                return UserPlanTypes.BASIC;
            }
            case 'premium': {
                return UserPlanTypes.PREMIUM;
            }
            case 'agency': {
                return UserPlanTypes.AGENCY;
            }
            case 'ai_premium': {
                return UserPlanTypes.AI_PREMIUM;
            }
            case 'api_starter': {
                return UserPlanTypes.FREE;
            }
            case 'api_pro': {
                return UserPlanTypes.FREE;
            }
            case 'custom': {
                return UserPlanTypes.CUSTOM;
            }
            default: {
                // NOTE: Should never happen
                return UserPlanTypes.NO_PLAN;
            }
        }
    }
});

export const userDropdownPlanSelector = createSelector(
    [userPlanSelector, userPlanTypeSelector],
    (userPlan, userPlanType) => (['api_starter', 'api_pro'].includes(userPlan) ? userPlan : userPlanType),
);

/**
 * Rank-check cadence.
 *
 * Mirrors the backend's single decision point, api.mangools.com
 * `workers/rank-tracker/utils/determineCrawlStrategy.js`:
 *  - `agency` is crawled every day (`agency_daily`),
 *  - every other subscription level - trial, entry, basic, premium, ai_premium and custom - is
 *    crawled on Sundays only (`paid_weekly`) and gets the previous rank copied on the other six
 *    days (`paid_copy`),
 *  - free and unknown plans are not crawled on schedule, they only ever get a copy (`free_copy`).
 *
 * On every plan a newly added keyword gets one first crawl; the API reports it, and any on-demand
 * refresh in flight, as `rank_check_pending` per keyword, which the table and the banner read.
 *
 * There is no `custom` exception: the backend special-cases `agency` and nothing else, so a
 * custom subscription is on the weekly side. A custom customer who really is on daily checks is
 * provisioned as `agency`.
 */
const SUNDAY = 0;

const WEEKLY_RANK_CHECK_PLANS = [
    UserPlanTypes.TRIAL,
    UserPlanTypes.ENTRY,
    UserPlanTypes.BASIC,
    UserPlanTypes.PREMIUM,
    UserPlanTypes.AI_PREMIUM,
    UserPlanTypes.CUSTOM,
];

export const hasDailyRankChecksSelector = createSelector(
    [userPlanTypeSelector],
    userPlanType => userPlanType === UserPlanTypes.AGENCY,
);

export const hasWeeklyRankChecksSelector = createSelector([userPlanTypeSelector], userPlanType =>
    WEEKLY_RANK_CHECK_PLANS.includes(userPlanType),
);

/**
 * Is a real rank check scheduled for today for this user?
 *
 * NOTE: deliberately not a `createSelector` - the answer depends on the current day as well as on
 * the plan, and the app stays open across midnight, so a memoized value keyed on the plan alone
 * would go stale. The day-of-week test is in UTC because that is how the backend picks its crawl
 * day (`moment.utc(pickedDate, 'YYYY-MM-DD').day() === 0`).
 *
 * Report routes treat every day as a check day (the store holds the viewer's plan, not the owner's),
 * but that is applied where the route is known reactively - TrackingContainer, TrackingLeftContainer
 * and TrackingRightContainer - not here; see the note at `isCheckPending` in TrackingContainer.
 */
export const isRankCheckScheduledTodaySelector = state =>
    hasDailyRankChecksSelector(state) || (hasWeeklyRankChecksSelector(state) && moment.utc().day() === SUNDAY);

/**
 * May a timeframe preset end today for this user?
 *
 * Only Agency gets a real crawl every day, so only for Agency is today genuinely "in progress".
 * Everyone else sees a copied (or, on Sundays, a freshly crawled) rank for today, so including
 * today shows real data - and it is what makes the on-demand refresh reachable at all.
 *
 * Report routes keep the yesterday-ending range; their callers AND the route into this.
 */
export const timeframeIncludesTodaySelector = createSelector(
    [hasDailyRankChecksSelector],
    hasDailyRankChecks => hasDailyRankChecks === false,
);

export const isWithUpsellModalForNewTrackingLocation = createSelector(userPlanTypeSelector, userPlan => {
    return [
        UserPlanTypes.NO_PLAN,
        UserPlanTypes.CUSTOM,
        UserPlanTypes.FREE,
        UserPlanTypes.TRIAL,
        UserPlanTypes.ENTRY,
    ].includes(userPlan);
});

export const newUserDiscountTimerLimitSelector = createSelector(
    [loggedInSelector, createdAtSelector, userPlanTypeSelector],
    (loggedIn, createdAt, userPlanType) => {
        // TODO: remove after debugging
        // return 15 * 60;

        // Only trial user can see this subscribe message, every new user will be TRIAL user
        if (loggedIn && !isNil(createdAt) && userPlanType === UserPlanTypes.TRIAL) {
            return DateTimeService.getTimeDifferenceInSeconds(
                createdAt + Defaults.USER_FRESH_LIMIT_MINUTES * 60 * 1000,
            );
        } else {
            return 0;
        }
    },
);

// Reads the clock when the user changes, so a campaign boundary shows after the next reload.
export const campaignBannerSelector = createSelector(
    [loggedInSelector, userPlanSelector, createdAtSelector],
    (loggedIn, plan, createdAt) =>
        CampaignService.getBanner({
            now: Date.now(),
            loggedIn,
            plan,
            createdAt,
            preview: CampaignService.readPreview(),
        }),
);

export const newUserDiscountActiveSelector = createSelector(
    [campaignBannerSelector, loggedInSelector, createdAtSelector, userPlanTypeSelector],
    (campaignBanner, loggedIn, createdAt, userPlanType) => {
        // TODO: remove after debugging
        // return true;

        if (!isNil(campaignBanner)) {
            return true;
        }

        // Only trial user can see this subscribe message, every new user will be TRIAL user
        if (loggedIn && !isNil(createdAt) && userPlanType === UserPlanTypes.TRIAL) {
            return DateTimeService.lessThanMinutesAgo(Defaults.USER_FRESH_LIMIT_MINUTES, createdAt);
        } else {
            return false;
        }
    },
);

export const whiteLabelSettingsSelector = state => state.user.settings.whiteLabel;
export const fetchingToggleWhitelabelSelector = state => state.user.fetchingWhitelabelToggle;
export const errorToggleWhitelabelStatusSelector = state => state.user.errorToggleWhitelabel.status;

// Looker Studio Token selectors
export const userLookerStudioTokenSelector = state => state.user.lookerStudio.lookerStudioToken;
export const fetchingUserLookerStudioTokenSelector = state => state.user.lookerStudio.fetching;
export const errorUserLookerStudioTokenSelector = state => state.user.lookerStudio.error;
